import { z } from "zod";
import { listMetaSchema, paginationSchema } from "./common.js";
import { siteGeofenceConfigSchema } from "./geofence.js";

export const siteStatusSchema = z.enum(["ACTIVE", "INACTIVE", "CLOSED"]);

const siteGeofenceFieldsSchema = z.object({
  latitude: z.number().min(-90).max(90).optional().nullable(),
  longitude: z.number().min(-180).max(180).optional().nullable(),
  geofenceRadiusMeters: z.coerce.number().int().min(10).max(50000).optional().nullable(),
  geofenceEnabled: z.boolean().default(false),
  geofenceStrictMode: z.boolean().default(false),
});

const siteBaseSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  code: z
    .string()
    .trim()
    .min(1, "Code is required")
    .max(32)
    .regex(/^[A-Za-z0-9-]+$/, "Code may only contain letters, numbers, and hyphens"),
  address: z.string().trim().max(500).optional().nullable(),
  status: siteStatusSchema.default("ACTIVE"),
  defaultPayRuleProfileId: z.string().uuid().optional().nullable(),
  managerIds: z.array(z.string().uuid()).default([]),
  notes: z.string().trim().max(5000).optional().nullable(),
});

function refineGeofenceEnabled<T extends z.ZodTypeAny>(schema: T) {
  return schema.superRefine((data, ctx) => {
    const row = data as z.infer<typeof siteGeofenceFieldsSchema>;
    if (!row.geofenceEnabled) return;
    if (row.latitude == null || row.longitude == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Latitude and longitude are required when geofencing is enabled",
        path: ["latitude"],
      });
    }
    if (row.geofenceRadiusMeters == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Geofence radius is required when geofencing is enabled",
        path: ["geofenceRadiusMeters"],
      });
    }
  });
}

export const createSiteSchema = refineGeofenceEnabled(
  siteBaseSchema.merge(siteGeofenceFieldsSchema),
);

export const updateSiteSchema = refineGeofenceEnabled(siteBaseSchema.merge(siteGeofenceFieldsSchema).partial());

export const archiveSiteSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const siteListQuerySchema = paginationSchema.extend({
  status: siteStatusSchema.optional(),
  includeArchived: z.coerce.boolean().optional().default(false),
});

export const siteManagerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
});

export const siteSummarySchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  address: z.string().nullable(),
  status: siteStatusSchema,
  defaultPayRuleProfileId: z.string().uuid().nullable(),
  defaultPayRuleProfileName: z.string().nullable(),
  notes: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  geofenceRadiusMeters: z.number().int().nullable(),
  geofenceEnabled: z.boolean(),
  geofenceStrictMode: z.boolean(),
  geofence: siteGeofenceConfigSchema,
  managers: z.array(siteManagerSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const siteDetailMetricsSchema = z.object({
  payPeriod: z
    .object({
      id: z.string().uuid(),
      label: z.string(),
      status: z.string(),
    })
    .nullable(),
  hours: z.object({
    total: z.number(),
    regular: z.number(),
    overtime: z.number(),
    doubleTime: z.number(),
  }),
  activeWorkers: z.number().int(),
  equipmentAssigned: z.number().int(),
  openExceptions: z.number().int(),
  costByJobCode: z.array(
    z.object({
      jobCode: z.string(),
      hours: z.number(),
      grossPay: z.number(),
    }),
  ),
  dailyEntries: z.array(
    z.object({
      id: z.string().uuid(),
      workerName: z.string(),
      hours: z.number(),
      status: z.string(),
      date: z.string(),
    }),
  ),
  exceptions: z.array(
    z.object({
      id: z.string().uuid(),
      type: z.string(),
      severity: z.string(),
      message: z.string(),
    }),
  ),
  auditActivity: z.array(
    z.object({
      id: z.string().uuid(),
      action: z.string(),
      actorName: z.string().nullable(),
      createdAt: z.string(),
      summary: z.string().nullable(),
    }),
  ),
});

export const siteDetailSchema = siteSummarySchema.extend({
  metrics: siteDetailMetricsSchema,
  canEdit: z.boolean(),
});

export const siteFormOptionsSchema = z.object({
  payRuleProfiles: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
    }),
  ),
  managers: z.array(siteManagerSchema),
});

export const siteListResponseSchema = z.object({
  data: z.array(siteSummarySchema),
  meta: listMetaSchema.extend({ resource: z.literal("sites") }),
});

export type SiteStatus = z.infer<typeof siteStatusSchema>;
export type CreateSiteInput = z.infer<typeof createSiteSchema>;
export type UpdateSiteInput = z.infer<typeof updateSiteSchema>;
export type SiteSummary = z.infer<typeof siteSummarySchema>;
export type SiteDetail = z.infer<typeof siteDetailSchema>;
export type SiteFormOptions = z.infer<typeof siteFormOptionsSchema>;
